import { Card } from '@ps/sports-ui-component-library';
import classNames from 'clsx';
import React, { memo, useRef } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import type { OpportunityInput } from '@swf/core/models/sports/types/betSlip';
import {
  getIsAvailableOpportunity,
  getIsRestrictedMinOddsOpportunity,
  getIsSgmOpportunity,
  getIsSingleAvailableOpportunity,
  getIsSingleOpportunity,
  getIsSingleUnavailableOpportunity
} from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import { select } from '@swf/core/store';
import type { MultipleOpportunity, SingleOpportunity } from '@swf/generated/types/api/sports';
import { Icon } from '@swf/sports/components';
import { OpportunityDescription } from '@swf/sports/components/OpportunityDescription/OpportunityDescription';
import isOddsBoostMarket from '@swf/sports/utils/isOddsBoostMarket';

import useDefaultStartingPriceSelected from '../../../../hooks/useDefaultStartingPriceSelected';
import { useRemoveOpportunityFieldOnUnmount } from '../../../../hooks/useRemoveOpportunityFieldOnUnmount';
import { calculatePotentialReturns } from '../../../../utils/calculatePotentialReturns';
import { calculateStake } from '../../../../utils/calculateStake';
import { calculateTotalPotentialReturns } from '../../../../utils/calculateTotalPotentialReturns';
import { getIconNameFromOpportunity } from '../../../../utils/getIconNameFromOpportunity';

import styles from './Opportunity.scss';
import BetDetails from './components/BetDetails/BetDetails';
import EachWayCheckbox from './components/EachWayCheckbox/EachWayCheckbox';
import ErrorBanner from './components/ErrorBanner/ErrorBanner';
import Heading from './components/Heading/Heading';
import MinOddsAlert from './components/MinOddsAlert/MinOddsAlert';
import PotentialReturn from './components/PotentialReturn/PotentialReturn';
import PromotionPicker from './components/PromotionPicker/PromotionPicker';
import SgmOpportunity from './components/SgmOpportunity/SgmOpportunity';
import Stake from './components/Stake/Stake';
import StakeAlerts from './components/StakeAlerts/StakeAlerts';
import UnavailableBanner from './components/UnavailableBanner/UnavailableBanner';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Opportunity: React.FC<Props> = ({ opportunity }) => {
  const opportunityRef = useRef<HTMLDivElement>(null);
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const defaultStartingPriceSelected = useRef(useDefaultStartingPriceSelected(opportunity));
  const shouldDisplayPromotionPicker = useSelector(select.betSlip.isPromotionPickerEnabled);

  useRemoveOpportunityFieldOnUnmount(opportunity.id);

  const {
    eachWaySelected,
    stakePerLineParsed: stakePerLine = 0,
    startingPriceSelected = defaultStartingPriceSelected.current,
    freeBet
  } = input.value || {};

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isRestrictedMinOddsOpportunity = getIsRestrictedMinOddsOpportunity(opportunity);
  const isSingleOpportunity = getIsSingleOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isSingleUnavailableOpportunity = getIsSingleUnavailableOpportunity(opportunity);
  const isSgmOpportunity = getIsSgmOpportunity(opportunity);
  const isFreeBet = !!freeBet?.length;

  let winPotentialReturn = 0;
  let placePotentialReturn = 0;
  let actualNumberOfLines = 0;

  if (isAvailableOpportunity) {
    winPotentialReturn = calculateTotalPotentialReturns({
      stakePerLine,
      placeReturnFactor: opportunity.placeReturnFactor,
      returnFactor: opportunity.returnFactor,
      numberOfLines: opportunity.numberOfLines,
      eachWaySelected,
      isFreeBet
    });

    placePotentialReturn = eachWaySelected
      ? calculatePotentialReturns({
          stake: stakePerLine,
          returnFactor: opportunity.placeReturnFactor ?? 0,
          numberOfLines: opportunity.numberOfLines,
          isFreeBet
        })
      : 0;

    actualNumberOfLines = eachWaySelected
      ? opportunity.numberOfLines * 2
      : opportunity.numberOfLines;
  }

  if (isSgmOpportunity) {
    return (
      <SgmOpportunity
        opportunityRef={opportunityRef}
        opportunity={opportunity}
        eachWaySelected={eachWaySelected}
        winPotentialReturn={winPotentialReturn}
        startingPriceSelected={startingPriceSelected}
      />
    );
  }

  return (
    <Card
      className={classNames({
        [styles.unavailable]: !isAvailableOpportunity
      })}
      data-testid="bet-slip-opportunity"
      ref={opportunityRef}
    >
      <div className={classNames(styles.mainInfo, !isSingleOpportunity && styles.multiple)}>
        <div className={styles.iconWrapper}>
          <Icon name={getIconNameFromOpportunity(opportunity)} size="xl" />
        </div>

        <div
          className={classNames(styles.heading, {
            [styles.headingWithBadge]:
              isSingleAvailableOpportunity && isOddsBoostMarket(opportunity.marketType)
          })}
        >
          <Heading opportunity={opportunity} opportunityRef={opportunityRef} />
        </div>

        {isSingleOpportunity && (
          <OpportunityDescription
            {...opportunity}
            className={styles.description}
            marketType={isSingleAvailableOpportunity ? opportunity.marketType : undefined}
          />
        )}

        <Stake
          className={styles.stake}
          opportunity={opportunity}
          eachWaySelected={eachWaySelected}
          actualNumberOfLines={actualNumberOfLines}
          winPotentialReturn={winPotentialReturn}
        />

        {isAvailableOpportunity && (
          <div className={styles.potentialReturn}>
            <PotentialReturn
              fieldName={opportunity.id}
              returnFactor={opportunity.returnFactor}
              numberOfLines={opportunity.numberOfLines}
              eachWaySelected={eachWaySelected}
              placeReturnFactor={opportunity.placeReturnFactor}
              opportunity={opportunity}
              startingPriceSelected={startingPriceSelected}
            />
          </div>
        )}

        {shouldDisplayPromotionPicker && (
          <PromotionPicker opportunity={opportunity} numberOfLines={actualNumberOfLines} />
        )}
      </div>

      {isAvailableOpportunity && (
        <StakeAlerts
          winRunnerOdds={opportunity.winRunnerOdds}
          id={opportunity.id}
          opportunity={opportunity}
          startingPriceSelected={startingPriceSelected}
        />
      )}

      {isRestrictedMinOddsOpportunity && <MinOddsAlert minOdds={opportunity.minOdds} />}

      {isAvailableOpportunity && opportunity.eachWayAvailable && (
        <div className={styles.additionalInfo}>
          <EachWayCheckbox
            fieldName={opportunity.id}
            placeTerms={isSingleOpportunity ? opportunity.placeTerms : undefined}
          />

          {eachWaySelected && (
            <BetDetails
              totalStake={calculateStake(stakePerLine, opportunity.numberOfLines, eachWaySelected)}
              winPotentialReturn={winPotentialReturn}
              placePotentialReturn={placePotentialReturn}
              opportunity={opportunity}
              startingPriceSelected={startingPriceSelected}
            />
          )}
        </div>
      )}

      {isSingleUnavailableOpportunity && <UnavailableBanner runnerId={opportunity.runnerId} />}

      <ErrorBanner fieldName={opportunity.id} />
    </Card>
  );
};

export default memo(Opportunity);
